/**
 * Metadata handling for product *cards* (PLP, search, peptide and stack-builder
 * cards) — deliberately separate from the PDP's `parseProductUxMetadata`.
 *
 * Why this exists: the list fragment used to select `metadata { key value }`,
 * which returns every key a product owns. Measured against the production API,
 * one listing page of 12 products was 851 KB, of which 98.4% was metadata and
 * 660 KB (94%) was a single key, `ux_market_copy_json` — the full per-channel,
 * per-locale marketing copy (~55 KB/product) fetched to render a card that uses
 * exactly one field of it, `shortDescription`.
 *
 * Two consequences, both bad: the response blew past Next's 2 MB data-cache
 * ceiling ("items over 2MB can not be cached"), so it was re-fetched on every
 * render and never cached, and every one of those bytes had to be parsed and
 * held through the render.
 *
 * The fix is a narrow `metafields(keys: [...])` selection plus `ux_card_copy_json`,
 * a slim `{channel: {locale: shortDescription}}` key derived from the big one by
 * `scripts/sync-card-copy-metadata.mjs` (37 KB instead of 660 KB per 12 products).
 * The channel level is load-bearing: 6 of 48 locale/channel pairs sampled had
 * genuinely different copy for eu vs us, so the channels cannot be collapsed.
 */

import { getContentFallbackChainOf } from "@/config/locale";

type MetadataEntry = {
	key?: string | null;
	value?: string | null;
};

/**
 * Every metadata key the card render path reads, and nothing else.
 *
 * This list is mirrored literally in `src/graphql/ProductListItem.graphql`
 * (GraphQL documents cannot import TypeScript constants). The drift guard in
 * `product-card-metadata.graphql.test.ts` fails if the two ever disagree.
 */
export const PRODUCT_CARD_METADATA_KEYS = [
	// parseProductUxMetadata — card subtitle and trust badges
	"ux_card_copy_json",
	"ux_short_description",
	"ux_trust_badges_json",
	// parsePeptideMetadata — peptide cards, quiz and stack builder
	"ux_research_level",
	"ux_peptide_scores_json",
	"ux_peptide_categories_json",
	"ux_availability",
	"ux_mechanism",
	"ux_typical_dosage",
	"ux_half_life",
	"ux_sequence",
	"ux_pubmed_url",
	"ux_aliases_json",
	"ux_timing",
	"ux_dosing_frequency",
	"ux_contraindicated_combinations_json",
] as const;

/** Channel used when a product has no bucket for the requested one. */
const FALLBACK_CHANNEL = "eu";
/** Locale used when a channel bucket has no entry for the requested locale. */
const FALLBACK_LOCALE = "en";

export type CardCopyMap = Record<string, Record<string, string>>;

/**
 * Adapt the `metafields` map onto the `{ key, value }[]` shape that
 * `parseProductUxMetadata` and `parsePeptideMetadata` already accept, so the
 * query change does not ripple into those parsers.
 */
export function metafieldsToEntries(metafields: Record<string, string> | null | undefined): MetadataEntry[] {
	if (!metafields) return [];
	return Object.entries(metafields).map(([key, value]) => ({ key, value }));
}

/**
 * Parse `ux_card_copy_json`. Non-string leaves are dropped rather than thrown
 * on: this key is machine-written, and one malformed locale must not blank the
 * subtitle for every other locale on the card.
 */
export function toCardCopyMap(raw: string | null | undefined): CardCopyMap | null {
	if (!raw) return null;

	let parsed: unknown;
	try {
		parsed = JSON.parse(raw);
	} catch {
		return null;
	}

	if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) return null;

	const result: CardCopyMap = {};
	for (const [channel, locales] of Object.entries(parsed)) {
		if (typeof locales !== "object" || locales === null || Array.isArray(locales)) continue;
		const bucket: Record<string, string> = {};
		for (const [locale, value] of Object.entries(locales)) {
			if (typeof value === "string") bucket[locale] = value;
		}
		result[channel] = bucket;
	}

	return result;
}

/**
 * Resolve the card subtitle for one channel/locale pair.
 *
 * Resolution order: exact channel then exact locale, falling back to the `eu`
 * channel and then to English within the resolved channel. A regional locale
 * tries its base locale before English (es-mx → es). Blank copy counts as
 * absent so the caller can fall through to its own default.
 */
export function selectCardShortDescription(
	raw: string | null | undefined,
	channel: string,
	locale: string,
): string | null {
	const map = toCardCopyMap(raw);
	if (!map) return null;

	const bucket = map[channel] ?? map[FALLBACK_CHANNEL];
	if (!bucket) return null;

	const chain = getContentFallbackChainOf(locale);
	const value =
		chain.map((candidate) => bucket[candidate]).find((entry) => entry !== undefined) ??
		bucket[FALLBACK_LOCALE];
	const trimmed = value?.trim();
	return trimmed ? trimmed : null;
}
